/**
 * Development Authentication Bypass Configuration
 * ===============================================
 *
 * This module enables automatic sign-in for local development against staging Supabase,
 * bypassing the manual login flow while maintaining full database connectivity and RLS policies.
 *
 * SECURITY NOTES:
 * - ONLY active in development mode (!import.meta.env.PROD)
 * - NEVER activates in production builds
 * - Requires valid staging user credentials
 * - Credentials stored in .env.local (gitignored)
 *
 * USAGE:
 * 1. Set environment variables in .env.local:
 *    VITE_DEV_AUTH_BYPASS=true
 *    VITE_DEV_USER_EMAIL=dev@coordi.local
 *    VITE_DEV_USER_PASSWORD=your-password
 *    VITE_DEV_SKIP_ONBOARDING=true
 *
 * 2. Start dev server: bun run dev
 * 3. Observe auto-login in console and bypass login screen
 *
 * @module devAuth
 * @since v2.0.0
 */


/**
 * Development authentication configuration object.
 *
 * All values are read from environment variables at build time.
 * This object is immutable and reflects the current dev environment setup.
 */
export const DEV_AUTH_CONFIG = {
  /** Master toggle - must be 'true' to enable dev auth bypass */
  enabled: import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',

  /** Dev user email address (must exist in staging Supabase) */
  email: import.meta.env.VITE_DEV_USER_EMAIL || '',

  /** Dev user password (never commit to git) */
  password: import.meta.env.VITE_DEV_USER_PASSWORD || '',

  /** Whether to skip the onboarding modal regardless of database state */
  skipOnboarding: import.meta.env.VITE_DEV_SKIP_ONBOARDING === 'true',

  /** Whether to skip contract extraction in onboarding (uses cached/mock data) */
  skipExtraction: import.meta.env.VITE_DEV_SKIP_EXTRACTION === 'true',
} as const;

/**
 * Determines if development authentication bypass is currently active.
 *
 * This function performs several safety checks:
 * 1. Must NOT be in production (import.meta.env.PROD === false)
 * 2. Must have VITE_DEV_AUTH_BYPASS=true
 * 3. Must have valid email and password configured
 *
 * @returns {boolean} True if dev auth bypass is enabled and safe to use
 *
 * @example
 * ```typescript
 * if (isDevAuthEnabled()) {
 *   console.log('Dev auth is active');
 *   // Auto-login logic here
 * }
 * ```
 */
export function isDevAuthEnabled(): boolean {
  // CRITICAL: Never enable in production, regardless of env vars
  if (import.meta.env.PROD) {
    return false;
  }

  // All three conditions must be met for dev auth to work
  return DEV_AUTH_CONFIG.enabled &&
         !!DEV_AUTH_CONFIG.email &&
         !!DEV_AUTH_CONFIG.password;
}

/**
 * Logs development authentication status to the console with visual indicators.
 *
 * This function provides clear feedback about the dev auth configuration and
 * helps developers understand why auto-login may or may not be working.
 *
 * @example
 * ```typescript
 * // In AuthContext initialization
 * if (isDevAuthEnabled()) {
 *   logDevModeStatus(); // Shows colorful dev mode indicator
 * }
 * ```
 */
export function logDevModeStatus(): void {
  if (isDevAuthEnabled()) {
    // Visual indicator with orange background (distinct from errors)
    console.log(
      '%c🔧 DEV AUTH MODE ACTIVE',
      'background: #ff9800; color: black; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );

    console.log(`   Email: ${DEV_AUTH_CONFIG.email}`);
    console.log(`   Skip Onboarding: ${DEV_AUTH_CONFIG.skipOnboarding}`);
    console.log(`   Skip Extraction: ${DEV_AUTH_CONFIG.skipExtraction}`);
    console.log(`   Production Guard: ${import.meta.env.PROD ? 'DISABLED' : 'ENABLED'}`);
    console.log('   → Auto-login will attempt on app startup');
  }
}

/**
 * DEVELOPMENT AUTHENTICATION BYPASS - IMPLEMENTATION GUIDE
 * =======================================================
 *
 * This feature allows developers to bypass manual authentication during local development
 * while maintaining full Supabase connectivity, RLS policies, and real user data.
 *
 * HOW IT WORKS:
 * 1. Environment variables configure a staging user for auto-login
 * 2. AuthContext checks for dev mode on app initialization
 * 3. If enabled, automatically signs in using the configured credentials
 * 4. User never sees login screen, but gets real JWT tokens
 * 5. Optional onboarding bypass prevents modal interruption
 *
 * SAFETY MEASURES:
 * - Production builds: Feature is completely disabled
 * - Credential validation: Won't attempt login with missing/invalid credentials
 * - Session reuse: Won't re-login if already authenticated
 * - Error handling: Falls back to normal auth flow on failure
 * - Git safety: Credentials stored in .env.local (gitignored)
 *
 * CONFIGURATION:
 * See .env.example for detailed variable documentation.
 *
 * TROUBLESHOOTING:
 * - Check console for "DEV AUTH MODE ACTIVE" message
 * - Verify VITE_DEV_USER_EMAIL and VITE_DEV_USER_PASSWORD are set
 * - Ensure dev user exists in staging Supabase
 * - Confirm VITE_DEV_AUTH_BYPASS=true
 * - Check that you're running in development mode (not production)
 */